/**
 * Subscription Status Check Hook
 *
 * Similar to the trial expiration check, this hook monitors subscription status
 * and handles lapsed/expired subscriptions by showing appropriate modals and
 * restricting access.
 */

import type { UserSubscription } from "@/models/subscription/subscription.model";
import { getCurrentSubscription } from "@/services/subscription-service";
import { useUserStore } from "@/stores/user-store";
import { useEffect, useRef, useState } from "react";

interface SubscriptionStatus {
	status: "active" | "canceled" | "past_due" | "expired" | "none";
	subscription: UserSubscription | null;
	daysUntilExpiration?: number;
	isLapsed: boolean;
}

export const useSubscriptionStatusCheck = () => {
	const [showLapsedModal, setShowLapsedModal] = useState(false);
	const [subscriptionStatus, setSubscriptionStatus] =
		useState<SubscriptionStatus | null>(null);
	const [isLoading, setIsLoading] = useState(false);
	const { user, setUser } = useUserStore();
	const hasCheckedRef = useRef(false);
	const checkTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const lastProcessedUserIdRef = useRef<number | undefined>(undefined);

	const checkSubscriptionStatus = async () => {
		// Don't check if user is not loaded yet or doesn't have essential data
		if (!user || !user.role) {
			return;
		}

		// Skip check for super admins (they get free unlimited access)
		if (user?.role?.access_level && user.role.access_level >= 1000) {
			setSubscriptionStatus({
				status: "active",
				subscription: null,
				isLapsed: false,
			});

			// Update user store with super admin subscription privileges
			// Only update if values have changed to prevent infinite loops
			if (
				!user.hasActiveSubscription ||
				!user.hasPlusSubscription ||
				user.subscriptionStatus !== "active"
			) {
				setUser({
					...user!,
					hasActiveSubscription: true,
					hasPlusSubscription: true, // Super admins get Plus features
					subscriptionStatus: "active" as any,
				});
			}
			return;
		}

		// Handle trial users (they have their own expiration system)
		// Check multiple possible trial flag names for compatibility
		if (user?.isTrial || user?.is_trial || user?.isTrialAccount) {
			// Check if trial is still valid (expiration date is in the future)
			const trialExpirationDate =
				user?.trialExpirationDate || user?.trial_expiration_date;

			if (trialExpirationDate) {
				const expirationDate = new Date(trialExpirationDate);
				const now = new Date();
				const isTrialValid = expirationDate > now;

				if (isTrialValid) {
					// Trial is still valid - set subscription status to active
					setSubscriptionStatus({
						status: "active",
						subscription: null,
						isLapsed: false,
					});

					// Update user store to indicate active subscription for valid trial users
					// Only update if values have changed to prevent infinite loops
					if (
						!user.hasActiveSubscription ||
						user.hasPlusSubscription !== false ||
						user.subscriptionStatus !== "active"
					) {
						setUser({
							...user!,
							hasActiveSubscription: true,
							hasPlusSubscription: false, // Trial users don't get Plus features
							subscriptionStatus: "active" as any,
						});
					}
					return;
				} else {
					// Trial has expired - don't set active subscription
					setSubscriptionStatus({
						status: "expired",
						subscription: null,
						isLapsed: true,
					});

					setUser({
						...user!,
						hasActiveSubscription: false,
						hasPlusSubscription: false,
						subscriptionStatus: null,
					});
					// Don't show subscription modal - trial expiration modal will handle this
					return;
				}
			} else {
				// Trial user but no expiration date yet - wait a bit and check again
				// This handles the case where user data is still loading
				// Set a temporary active status to prevent blocking
				setSubscriptionStatus({
					status: "active",
					subscription: null,
					isLapsed: false,
				});

				// Only update if values have changed to prevent infinite loops
				if (
					!user.hasActiveSubscription ||
					user.hasPlusSubscription !== false ||
					user.subscriptionStatus !== "active"
				) {
					setUser({
						...user!,
						hasActiveSubscription: true,
						hasPlusSubscription: false,
						subscriptionStatus: "active" as any,
					});
				}

				// Re-check after a short delay to see if expiration date is now available
				// Only do this once to prevent infinite loops
				if (!hasCheckedRef.current) {
					hasCheckedRef.current = true;
					if (checkTimeoutRef.current) {
						clearTimeout(checkTimeoutRef.current);
					}
					checkTimeoutRef.current = setTimeout(() => {
						hasCheckedRef.current = false;
						checkSubscriptionStatus();
					}, 1000);
				}
				return;
			}
		}

		try {
			setIsLoading(true);
			const [subscription, plans] = await Promise.all([
				getCurrentSubscription(),
				import("@/services/subscription-service").then((m) =>
					m.getSubscriptionPlans(),
				),
			]);

			// Check if organization is exempt from subscription requirements
			if (subscription && (subscription as any).isExempt === true) {
				// Organization is exempt - treat as active subscription
				setSubscriptionStatus({
					status: "active",
					subscription: null,
					isLapsed: false,
				});

				// Update user store to indicate active subscription for exempt organizations
				setUser({
					...user!,
					hasActiveSubscription: true,
					hasPlusSubscription: true, // Exempt organizations get Plus features
					subscriptionStatus: "active" as any,
				});
				return;
			}

			if (!subscription) {
				// No subscription found - user needs to subscribe
				setSubscriptionStatus({
					status: "none",
					subscription: null,
					isLapsed: true,
				});
				setShowLapsedModal(true);
				return;
			}

			const now = new Date();
			const periodEnd = new Date(subscription.currentPeriodEnd);
			const daysUntilExpiration = Math.ceil(
				(periodEnd.getTime() - now.getTime()) / (1000 * 60 * 60 * 24),
			);

			let isLapsed = false;
			let status: SubscriptionStatus["status"] = subscription.status as any;

			// Check if subscription is past due or expired
			if (
				subscription.status === "past_due" ||
				subscription.status === "canceled" ||
				(subscription.status === "active" && daysUntilExpiration < 0)
			) {
				isLapsed = true;
				status = subscription.status === "canceled" ? "canceled" : "past_due";
				setShowLapsedModal(true);
			}

			setSubscriptionStatus({
				status,
				subscription,
				daysUntilExpiration,
				isLapsed,
			});

			// Find the current plan details
			const currentPlan = plans.find(
				(plan) => plan.id.toString() === subscription.planId.toString(),
			);

			// Check if this is a Plus subscription by plan name or ID
			const isPlusSubscription =
				subscription.status === "active" &&
				(currentPlan?.name
					?.toLowerCase()
					.includes("plus") || // Plus plan by name
					Number(subscription.planId) === 1 || // Super Admin plan (gets Plus features)
					Number(subscription.planId) === 3 || // FacilityAI Plus Subscription plan
					Number(subscription.planId) === 17); // Legacy plan ID

			// Update user store with subscription info
			setUser({
				...user!,
				hasActiveSubscription: !isLapsed && subscription.status === "active",
				hasPlusSubscription: !isLapsed && isPlusSubscription,
				subscriptionStatus: subscription.status as any,
			});
		} catch (error: any) {
			console.error("Failed to check subscription status:", error);

			// Check if error is because user is on trial (404 with "Trial user" message)
			const isTrialUserError =
				error?.message?.includes("Trial user") ||
				error?.response?.data?.message?.includes("Trial user");

			// If error indicates trial user, treat them as having active subscription
			if (isTrialUserError) {
				setSubscriptionStatus({
					status: "active",
					subscription: null,
					isLapsed: false,
				});

				// Only update if values have changed to prevent infinite loops
				if (
					!user.hasActiveSubscription ||
					user.hasPlusSubscription !== false ||
					user.subscriptionStatus !== "active"
				) {
					setUser({
						...user!,
						hasActiveSubscription: true,
						hasPlusSubscription: false,
						subscriptionStatus: "active" as any,
					});
				}
				return;
			}

			// If we can't check subscription status and user is not trial/super admin,
			// assume subscription is lapsed
			if (
				!isTrialUserError &&
				!user?.isTrial &&
				!user?.is_trial &&
				!user?.isTrialAccount &&
				(!user?.role?.access_level || user.role.access_level < 1000)
			) {
				setSubscriptionStatus({
					status: "none",
					subscription: null,
					isLapsed: true,
				});
				setShowLapsedModal(true);
			}
		} finally {
			setIsLoading(false);
		}
	};

	useEffect(() => {
		// Check subscription status when component mounts and user changes
		// Include trial flags and expiration date in dependencies to re-check if user trial status changes
		// Also include user.role to ensure we wait for user data to be fully loaded
		if (user && user.role) {
			// Only check if user ID has changed OR if this is the first time we're seeing this user with a role
			// This prevents infinite loops from subscription status updates while still checking when role loads
			const shouldCheck =
				lastProcessedUserIdRef.current !== user.id ||
				(lastProcessedUserIdRef.current === user.id && !hasCheckedRef.current);

			if (shouldCheck) {
				lastProcessedUserIdRef.current = user.id;
				// Reset the check flag when user changes
				hasCheckedRef.current = true;
				if (checkTimeoutRef.current) {
					clearTimeout(checkTimeoutRef.current);
					checkTimeoutRef.current = null;
				}
				checkSubscriptionStatus();
			}
		}

		// Cleanup timeout on unmount
		return () => {
			if (checkTimeoutRef.current) {
				clearTimeout(checkTimeoutRef.current);
			}
		};
	}, [
		user?.id,
		user?.role?.access_level,
		user?.organization_id,
		user?.isTrial,
		user?.is_trial,
		user?.isTrialAccount,
		user?.trialExpirationDate,
		user?.trial_expiration_date,
	]);

	const closeLapsedModal = () => {
		setShowLapsedModal(false);
	};

	return {
		showLapsedModal,
		closeLapsedModal,
		subscriptionStatus,
		isLoading,
		checkSubscriptionStatus,
	};
};
